// src/pages/event-content/OrganizersPage.tsx

import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import RichTextEditor from "../../components/ui/RichTextEditor";
import Switch from "../../components/ui/Switch";
import {
    createOrganizerRequest,
    getOrganizersRequest,
    updateOrganizerRequest,
} from "../../services/api/event-content.api";
import { OrganizerItem } from "../../services/types/event-content.types";

interface OrganizerFormState {
    name: string;
    description: string;
    isActive: boolean;
    isOurCompany: boolean;
}

const initialFormState: OrganizerFormState = {
    name: "",
    description: "",
    isActive: true,
    isOurCompany: false,
};

const OrganizersPage: React.FC = () => {
    const [items, setItems] = useState<OrganizerItem[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formState, setFormState] = useState<OrganizerFormState>(initialFormState);

    const [searchValue, setSearchValue] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const filteredItems = useMemo(() => {
        const query = searchValue.trim().toLowerCase();

        if (!query) {
            return items;
        }

        return items.filter((item) => {
            const name = item.name?.toLowerCase() ?? "";
            const description = item.description?.toLowerCase() ?? "";

            return name.includes(query) || description.includes(query);
        });
    }, [items, searchValue]);

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const response = await getOrganizersRequest();
            setItems(response);

            if (selectedId) {
                const selectedItem = response.find((item) => item.id === selectedId);

                if (!selectedItem) {
                    setSelectedId(null);
                    setFormState(initialFormState);
                } else {
                    setFormState({
                        name: selectedItem.name ?? "",
                        description: selectedItem.description ?? "",
                        isActive: selectedItem.is_active,
                        isOurCompany: selectedItem.is_our_company,
                    });
                }
            }
        } catch (error) {
            setPageError(
                error instanceof Error ? error.message : "Не удалось загрузить организаторов.",
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetForm = () => {
        setSelectedId(null);
        setFormState(initialFormState);
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleSelect = (item: OrganizerItem) => {
        setSelectedId(item.id);
        setFormState({
            name: item.name ?? "",
            description: item.description ?? "",
            isActive: item.is_active,
            isOurCompany: item.is_our_company,
        });
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);
        setSuccessMessage(null);

        if (!formState.name.trim()) {
            setFormError("Название организатора обязательно.");
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                name: formState.name.trim(),
                description: formState.description.trim() || null,
                is_active: formState.isActive,
                is_our_company: formState.isOurCompany,
            };

            const savedItem = selectedId
                ? await updateOrganizerRequest(selectedId, payload)
                : await createOrganizerRequest(payload);

            if (selectedId) {
                setItems((prev) =>
                    prev.map((item) => (item.id === savedItem.id ? savedItem : item)),
                );
                handleSelect(savedItem);
                setSuccessMessage("Организатор обновлён.");
            } else {
                setItems((prev) => [savedItem, ...prev]);
                handleSelect(savedItem);
                setSuccessMessage("Организатор создан.");
            }
        } catch (error) {
            setFormError(
                error instanceof Error ? error.message : "Ошибка сохранения организатора",
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Организаторы"
                description="Справочник организаторов мероприятий (одно название и описание, без языковых версий)."
                actions={<Button onClick={resetForm}>Новый организатор</Button>}
            />

            <section className="content-split artist-layout">
                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">Список организаторов</h3>
                            <p className="card-text">
                                Организаторы, которые могут быть привязаны к турам и событиям.
                            </p>
                        </div>

                        <div className="section-toolbar-controls">
                            <Input
                                type="text"
                                placeholder="Поиск по названию или описанию"
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />
                        </div>
                    </div>

                    {pageError ? (
                        <div className="form-global-error">{pageError}</div>
                    ) : isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Организаторы не найдены</h3>
                            <p className="empty-block-text">
                                Создайте первого организатора или измените поисковый запрос.
                            </p>
                        </div>
                    ) : (
                        <div className="artist-list-grid">
                            {filteredItems.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`artist-list-item artist-list-item-compact ${
                                        item.id === selectedId ? "artist-list-item-active" : ""
                                    }`}
                                    onClick={() => handleSelect(item)}
                                >
                                    <div className="artist-list-content">
                                        <span className="artist-list-title">
                                            {item.name}
                                        </span>
                                    </div>

                                    <div className="inline-actions">
                                        <span
                                            className={`status-dot ${
                                                item.is_active
                                                    ? "status-dot-active"
                                                    : "status-dot-inactive"
                                            }`}
                                        />
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </Card>

                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">
                                {selectedId ? "Редактирование организатора" : "Новый организатор"}
                            </h3>
                            <p className="card-text">
                                Здесь можно задать название организатора, описание, активность и отметить, является ли он собственной компанией.
                            </p>
                        </div>
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        <Field label="Название" htmlFor="organizer-name">
                            <Input
                                id="organizer-name"
                                value={formState.name}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        name: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <RichTextEditor
                            label="Описание"
                            value={formState.description}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, description: value }))
                            }
                        />

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isActive}
                                onChange={(checked) =>
                                    setFormState((prev) => ({ ...prev, isActive: checked }))
                                }
                                label={
                                    formState.isActive
                                        ? "Организатор активен"
                                        : "Организатор выключен"
                                }
                            />
                        </div>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isOurCompany}
                                onChange={(checked) =>
                                    setFormState((prev) => ({ ...prev, isOurCompany: checked }))
                                }
                                label={
                                    formState.isOurCompany
                                        ? "Собственная компания"
                                        : "Внешний организатор"
                                }
                            />
                        </div>

                        {formError ? <div className="form-global-error">{formError}</div> : null}
                        {successMessage ? (
                            <div className="form-global-success">{successMessage}</div>
                        ) : null}

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving}>
                                {isSaving
                                    ? "Сохраняем..."
                                    : selectedId
                                        ? "Сохранить изменения"
                                        : "Создать организатора"}
                            </Button>

                            <Button
                                type="button"
                                variant="ghost"
                                disabled={isSaving}
                                onClick={resetForm}
                            >
                                Очистить форму
                            </Button>
                        </div>
                    </form>
                </Card>
            </section>
        </div>
    );
};

export default OrganizersPage;
